import { parseISO } from "date-fns";
import AgentDomain from "entities/domain/agents/agent-domain";
import AgentNotificationConfigDomain from "entities/domain/agents/agent-notification-config-domain";
import MerchantAgentDomain from "entities/domain/merchant-agents/merchant-agent-domain";
import AgentPermissionsDomain from "entities/domain/roles/agent-permissions-domain";
import AgentRolesDomain, {
  AgentRole,
} from "entities/domain/roles/agent-roles-domain";
import { AdminAgentDTO } from "entities/dto/AdminAgentDTO";
import { AgentDTO } from "entities/dto/AgentDTO";
import { AgentPermissionsDTO, AgentRoleDTO } from "entities/dto/AgentRoleDTO";
import { colorList } from "util/methods";

const transformNotificationConfigfromDtoToDomain = (
  d: AgentDTO["notification_config"]
): AgentNotificationConfigDomain | null => {
  if (!d) return null;
  return new AgentNotificationConfigDomain(
    d.handle,
    d.channel,
    d.preference,
    d.strategy ?? "",
    d.timezone,
    d.disabled_channels ?? [],
    d.custom_configs ?? []
  );
};

export const agentTransformFromDtoToDomain = (
  agent: AgentDTO,
  index: number = 0
): MerchantAgentDomain =>
  new MerchantAgentDomain({
    id: agent.id,
    name: agent.name,
    surname: agent.surname,
    email: agent.email,
    phone: agent.phone,
    preferredContactChannel: agent.contact_channel,
    notificationConfig: transformNotificationConfigfromDtoToDomain(
      agent.notification_config
    ),
    merchantId: agent.merchant_id,
    userId: agent.user_id,
    profilePic: agent.profile_picture,
    color: colorList[index % colorList.length],
    role: agent.role
      ? {
          id: agent.role.id,
          name: agent.role.name,
          description: agent.role.description,
        }
      : null,
    permissions:
      agent.permissions?.map((p) => ({
        id: p.id,
        name: p.name,
        description: p.description,
      })) || null,
    active: agent.active,
    rotationEnabled: agent.rotation_enabled,
    playerIds: agent.player_ids,
    stylePreferences: agent.style_preferences
      ? {
          chatBackground: agent.style_preferences.chat_background,
          colorScheme: agent.style_preferences.color_scheme,
        }
      : null,
    lastActiveAt: agent.last_active_at
      ? parseISO(
          agent.last_active_at.includes("Z")
            ? agent.last_active_at
            : `${agent.last_active_at}Z`
        )
      : null,
    mfaEnrolledAt: agent.mfa_enrolled_at
      ? parseISO(
          agent.mfa_enrolled_at.includes("Z")
            ? agent.mfa_enrolled_at
            : `${agent.mfa_enrolled_at}Z`
        )
      : null,
  });

export const agentTransformFromAdminDtoToAgentDomain = (
  admin: AdminAgentDTO,
  index: number = 0,
  merchantId?: number
): MerchantAgentDomain => {
  const merchant = merchantId
    ? admin.merchants.find((m) => m.id === merchantId)
    : admin.merchants[0];

  return new MerchantAgentDomain({
    id: admin.id,
    name: admin.name,
    surname: admin.surname,
    email: admin.email,
    phone: admin.phone,
    preferredContactChannel: admin.contact_channel,
    notificationConfig: admin.notification_config
      ? new AgentNotificationConfigDomain(
          admin.notification_config.handle,
          admin.notification_config.channel,
          admin.notification_config.preference,
          "",
          admin.notification_config.timezone
        )
      : null,
    merchantId: merchant ? merchant.id : 0,
    userId: admin.user_id,
    profilePic: admin.profile_picture,
    color: colorList[index % colorList.length],
    role: merchant
      ? { id: merchant.role.id, name: merchant.role.name, description: "" }
      : null,
    permissions: null,
    active: merchant ? merchant.is_active : true,
    rotationEnabled: merchant ? merchant.rotation_enabled : false,
    playerIds: admin.player_ids,
    stylePreferences: admin.style_preferences
      ? {
          chatBackground: admin.style_preferences.chat_background,
          colorScheme: admin.style_preferences.color_scheme,
        }
      : null,
    lastActiveAt: admin.last_active_at
      ? parseISO(
          admin.last_active_at.includes("Z")
            ? admin.last_active_at
            : `${admin.last_active_at}Z`
        )
      : null,
    mfaEnrolledAt: null,
  });
};

export const agentTransformFromAdminDomainToAgentDomain = (
  admin: AgentDomain,
  merchantId: number
): MerchantAgentDomain => {
  const role = admin.getRole(merchantId);
  return new MerchantAgentDomain({
    id: admin.id,
    name: admin.name,
    surname: admin.surname,
    email: admin.email,
    phone: admin.phone,
    preferredContactChannel: admin.preferredContactChannel,
    notificationConfig: admin.notificationConfig,
    merchantId,
    userId: admin.userId,
    profilePic: admin.profilePic,
    color: admin.color,
    role: role ? { id: role.id, name: role.name, description: "" } : null,
    permissions: null,
    active: admin.isActive(merchantId),
    rotationEnabled: admin.rotationEnabled(merchantId),
    playerIds: admin.playerIds,
    stylePreferences: admin.stylePreferences
      ? {
          chatBackground: admin.stylePreferences.chatBackground,
          colorScheme: admin.stylePreferences.colorScheme,
        }
      : null,
    lastActiveAt: admin.lastActiveAt,
    mfaEnrolledAt: null,
  });
};

export const agentRolesTransformFromDtoToDomain = (
  role: AgentRoleDTO
): AgentRolesDomain =>
  new AgentRolesDomain(
    role.id,
    role.name as AgentRole,
    role.description,
    role.permissions
  );

export const agentPermissionsTransformFromDtoToDomain = (
  p: AgentPermissionsDTO
): AgentPermissionsDomain =>
  new AgentPermissionsDomain(p.id, p.name, p.description, p.group);
